import { useNavigate } from '@remix-run/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import useMediaQuery from '#app/hooks/useMediaQuery'
import { cn } from '#app/utils/misc'
import PlentyBody from './typography/plenty-body'
import { Icon } from './ui/icon'

const separatorStyles = 'mx-2 text-gray-500'

interface BreadcrumbProps {
	pathSegments: string[]
	brandParams?: string[]
	storeParams?: string[]
	bodyTypeParams?: string[]
	genderParams?: string[]
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
	pathSegments,
	brandParams,
	storeParams,
	bodyTypeParams,
	genderParams,
}) => {
	const navigate = useNavigate()
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const isDesktop = useMediaQuery({ mobile: '820px' })
	const { t, ready } = useTranslation('categories')
	const params = []

	if (!ready) return <div>Loading...</div> // Or some other loading indicator
	if (brandParams) {
		const brandParamsString = brandParams
			.map(brand => `brand=${encodeURIComponent(brand)}`)
			.join('&')
		if (brandParamsString) {
			params.push(brandParamsString)
		}
	}

	if (storeParams) {
		const storeParamsString = storeParams
			.map((store: string) => `${encodeURIComponent(store)}`)
			.join(',')
		if (storeParamsString) {
			params.push(`store=${storeParamsString}`)
		}
	}

	if (bodyTypeParams) {
		const bodyTypeParamsString = bodyTypeParams
			.map(bodyType => `bodytype=${encodeURIComponent(bodyType)}`)
			.join('&')
		if (bodyTypeParamsString) {
			params.push(bodyTypeParamsString)
		}
	}

	if (genderParams) {
		const genderParamsString = genderParams
			.map(gender => `gender=${encodeURIComponent(gender)}`)
			.join('&')
		if (genderParamsString) {
			params.push(genderParamsString)
		}
	}

	const paramsForUrl = params.length > 0 ? `?${params.join('&')}` : ''

	const terms = pathSegments.map(segment => segment)

	const unFormatSegment = (segment: string) => {
		if (segment.includes('(') && segment.includes(')')) {
			const start = segment.indexOf('(')
			const end = segment.indexOf(')')
			const rangePart = segment.substring(start + 1, end).replace('-', '_')
			segment = segment.substring(0, start).trim() + '_' + rangePart

			// Convert spaces back to underscores for non-range parts and ensure lowercase
			segment = segment.replace(/\s+/g, '_').toLowerCase()

			// Remove any added spaces before uppercase letters not followed by lowercase (acronyms)
			segment = segment.replace(/_([A-Z]+)_([A-Z][a-z])/g, '$1$2')
			return segment
		} else {
			return segment.replace(/&/g, 'and').replace(/\s/g, '')
		}
	}

	const createPath = (index: number) => {
		const pathSegment = pathSegments
			.slice(0, index + 1)
			.map(unFormatSegment)
			.join('-')
			.toLowerCase()
		return paramsForUrl
			? `/products/${pathSegment}${paramsForUrl}`
			: `/products/${pathSegment}`
	}

	// is mobile and have more than 5 paths
	// we are going to keep the first one and left the last 2

	if (!isDesktop && pathSegments.length > 3) {
		// terms.splice(0, 1)
		terms.splice(1, pathSegments.length)
		terms.push('...', pathSegments[pathSegments.length - 1])
	}

	const translatedTerms = terms.map(term => t(term))

	return (
		<nav aria-label="Breadcrumb" className="min-h-6">
			<ol className="flex items-center overflow-hidden overflow-x-auto whitespace-nowrap scrollbar-hide ">
				{translatedTerms.map((term, index) => {
					const isLastItem = index === terms.length - 1 // Check if it's the last item
					const pathTo = createPath(index)
					return (
						<React.Fragment key={`${term}-${index}`}>
							<li
								onClick={
									!isLastItem
										? () => {
												trackAmplitudeEvent('pdp breadcrumb click', {
													pathTo,
												})
												navigate(pathTo)
										  }
										: undefined
								}
								className={cn(
									'group',
									!isLastItem ? 'cursor-pointer' : 'cursor-default',
								)}
								data-testid={`breadcrumb-${index}`}
							>
								<PlentyBody
									size="xs"
									className={cn(isLastItem ? 'font-bold' : '')}
								>
									{term}
								</PlentyBody>
								<div
									className={cn(
										'h-[1px] w-0 rounded-lg bg-black-90 transition-all ',
										!isLastItem ? 'group-hover:w-full' : '',
									)}
								></div>
							</li>
							{index < terms.length - 1 && (
								<span className={separatorStyles} aria-hidden="true">
									<Icon name="slash" size="md" />
								</span>
							)}
						</React.Fragment>
					)
				})}
			</ol>
		</nav>
	)
}

export default Breadcrumb
